import React, { Component } from "react";
import { FilePond, registerPlugin } from "react-filepond";
import "filepond/dist/filepond.min.css";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginImageCrop from 'filepond-plugin-image-crop';
import FilePondPluginImageValidateSize from 'filepond-plugin-image-validate-size';
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
  FilePondPluginImageCrop,
  FilePondPluginImageValidateSize
);

/**
 * Default allowed file types - aligned with backend RequestMiddleware
 */
const DEFAULT_ACCEPTED_FILE_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/vnd.ms-powerpoint', // .ppt
  'application/pdf', // .pdf
  'image/png', // .png
  'image/jpeg', // .jpg, .jpeg
];

/**
 * Human-readable labels for file types
 */
const FILE_TYPE_LABELS = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/pdf': '.pdf',
  'image/png': '.png',
  'image/jpeg': '.jpg/.jpeg',
};

const DEFAULT_MAX_TOTAL_SIZE = 30 * 1024 * 1024; // 30MB
const DEFAULT_MAX_FILE_SIZE = '20MB';

class MultipleFileInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      totalSizeError: null,
      validFiles: [],
      invalidFiles: [],
    };
    this.filePondRef = React.createRef();
  }

  getAcceptedFileTypes = () => {
    if (this.props.acceptedFileTypes && this.props.acceptedFileTypes.length > 0) {
      return this.props.acceptedFileTypes;
    }
    return DEFAULT_ACCEPTED_FILE_TYPES;
  };

  getMaxTotalSize = () => {
    return this.props.maxTotalSize || DEFAULT_MAX_TOTAL_SIZE;
  };

  getMaxFileSize = () => {
    const maxSize = this.props.maxFileSize || DEFAULT_MAX_FILE_SIZE;
    if (typeof maxSize === 'string') {
      const match = maxSize.match(/^(\d+)(MB|KB|GB)$/i);
      if (match) {
        const value = parseInt(match[1]);
        const unit = match[2].toUpperCase();
        const multipliers = { KB: 1024, MB: 1024 * 1024, GB: 1024 * 1024 * 1024 };
        return value * multipliers[unit];
      }
    }
    return maxSize;
  };

  formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  getAllowedTypesLabel = () => {
    const acceptedTypes = this.getAcceptedFileTypes();
    const labels = acceptedTypes.map(type => FILE_TYPE_LABELS[type] || type);
    return labels.join(', ');
  };

  /**
   * Validate a single file
   */
  validateFile = (file) => {
    const acceptedTypes = this.getAcceptedFileTypes();
    const maxFileSize = this.getMaxFileSize();

    if (!acceptedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file type. Allowed: ${this.getAllowedTypesLabel()}`,
      };
    }

    if (file.size > maxFileSize) {
      return {
        valid: false,
        error: `File exceeds maximum size of ${this.props.maxFileSize || DEFAULT_MAX_FILE_SIZE}`,
      };
    }

    return { valid: true, error: null };
  };

  /**
   * Validate all files and check total size
   */
  validateAllFiles = (fileItems) => {
    const validFiles = [];
    const invalidFiles = [];
    const errors = [];
    let totalSize = 0;

    fileItems.forEach((fileItem) => {
      const file = fileItem.file;
      if (!file) return;

      const validation = this.validateFile(file);

      if (validation.valid) {
        validFiles.push(fileItem);
        totalSize += file.size;
      } else {
        invalidFiles.push({
          fileItem,
          error: validation.error,
        });
        errors.push(`"${file.name}": ${validation.error}`);
      }
    });

    const maxTotalSize = this.getMaxTotalSize();
    let totalSizeError = null;

    if (totalSize > maxTotalSize) {
      totalSizeError = `Total file size (${this.formatFileSize(totalSize)}) exceeds maximum allowed (${this.formatFileSize(maxTotalSize)})`;
      errors.push(totalSizeError);
    }

    return {
      isValid: invalidFiles.length === 0 && !totalSizeError,
      errors,
      validFiles,
      invalidFiles,
      totalSize,
      totalSizeError,
    };
  };

  /**
   * Handle file updates
   */
  handleUpdateFiles = (fileItems) => {
    const validation = this.validateAllFiles(fileItems);

    this.setState({
      totalSizeError: validation.totalSizeError,
      validFiles: validation.validFiles,
      invalidFiles: validation.invalidFiles,
    });

    // CRITICAL: Notify parent about validation state
    if (this.props.onValidationChange) {
      this.props.onValidationChange({
        isValid: validation.isValid,
        errors: validation.errors,
        validFiles: validation.validFiles.map(f => f.file),
        totalSize: validation.totalSize,
      });
    }

    // Call parent's onupdatefiles
    if (this.props.onupdatefiles) {
      this.props.onupdatefiles(fileItems);
    }
  };

  /**
   * Get only valid files - call via ref: ref.current.getValidFiles()
   */
  getValidFiles = () => {
    return this.state.validFiles.map(f => f.file);
  };

  /**
   * Check if all files are valid - call via ref: ref.current.isValid()
   */
  isValid = () => {
    const fileItems = this.props.files || [];
    if (fileItems.length === 0) return true;

    const validation = this.validateAllFiles(
      fileItems.map(f => ({ file: f.file || f }))
    );
    return validation.isValid;
  };

  /**
   * Get validation errors - call via ref: ref.current.getErrors()
   */
  getErrors = () => {
    const fileItems = this.props.files || [];
    if (fileItems.length === 0) return [];

    const validation = this.validateAllFiles(
      fileItems.map(f => ({ file: f.file || f }))
    );
    return validation.errors;
  };

  labelFileTypeNotAllowed = () => {
    return `Invalid file type. Allowed: ${this.getAllowedTypesLabel()}`;
  };

  render() {
    const acceptedFileTypes = this.getAcceptedFileTypes();
    const { totalSizeError, invalidFiles } = this.state;
    const hasErrors = totalSizeError || invalidFiles.length > 0;

    return (
      <div className="multiple-file-input-wrapper">
        {/* Error messages */}
        {hasErrors && (
          <div
            className="filepond-validation-errors"
            style={{
              backgroundColor: '#fee2e2',
              border: '1px solid #ef4444',
              borderRadius: '4px',
              padding: '10px 12px',
              marginBottom: '10px',
              color: '#dc2626',
              fontSize: '14px',
            }}
          >
            <strong>⚠️ Please fix the following errors before submitting:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              {invalidFiles.map((item, index) => (
                <li key={index}>
                  <strong>{item.fileItem.file?.name}</strong>: {item.error}
                </li>
              ))}
              {totalSizeError && <li>{totalSizeError}</li>}
            </ul>
          </div>
        )}

        <FilePond
          ref={this.props.innerRef || this.filePondRef}
          files={this.props.files}
          allowMultiple={this.props.allowMultiple}
          maxFiles={this.props.maxFiles || 10}
          onupdatefiles={this.handleUpdateFiles}
          credits={false}

          // File type validation
          allowFileTypeValidation={true}
          acceptedFileTypes={acceptedFileTypes}
          labelFileTypeNotAllowed={this.labelFileTypeNotAllowed()}
          fileValidateTypeLabelExpectedTypes={`Allowed: ${this.getAllowedTypesLabel()}`}

          // File size validation (per file)
          allowFileSizeValidation={true}
          maxFileSize={this.props.maxFileSize || DEFAULT_MAX_FILE_SIZE}
          labelMaxFileSizeExceeded="File is too large"
          labelMaxFileSize={`Maximum file size is ${this.props.maxFileSize || DEFAULT_MAX_FILE_SIZE}`}

          // Image size validation
          allowImageValidateSize={this.props.imageValidateSizeMaxWidth ? true : false}
          imageValidateSizeMaxWidth={this.props.imageValidateSizeMaxWidth || null}
          imageValidateSizeMaxHeight={this.props.imageValidateSizeMaxHeight || null}

          // Error handling
          onerror={this.props.onErrorCaught}
          beforeRemoveFile={() => true}

          // Server configuration
          server={{
            load: (source, load, error, progress, abort, headers) => {
              const myRequest = new Request(source);
              fetch(myRequest)
                .then((response) => {
                  response.blob().then((myBlob) => {
                    load(myBlob);
                  });
                })
                .catch((err) => {
                  error('Failed to load file');
                });
            },
          }}

          labelIdle={this.props.labelIdle || 'Drag & Drop your files or <span class="filepond--label-action">Browse</span>'}
        />

        {/* Allowed types hint */}
        {this.props.showAllowedTypes !== false && (
          <div
            className="filepond-allowed-types-hint"
            style={{
              fontSize: '12px',
              color: '#6b7280',
              marginTop: '6px',
            }}
          >
            Allowed: {this.getAllowedTypesLabel()}
          </div>
        )}
      </div>
    );
  }
}

MultipleFileInput.defaultProps = {
  allowMultiple: true,
  maxFiles: 10,
  maxFileSize: DEFAULT_MAX_FILE_SIZE,
  maxTotalSize: DEFAULT_MAX_TOTAL_SIZE,
  showAllowedTypes: true,
};

export default MultipleFileInput;